/**
 * GRN (Goods Receipt Note) Configuration
 * Centralized configurable values for GRN module
 * These values can be modified based on environment or business requirements
 */

// ============================================
// CURRENCY CONFIGURATION
// ============================================
export const CURRENCY_CONFIG = {
  // Primary currency code (can be changed for multi-currency support)
  CODE: process.env.REACT_APP_CURRENCY_CODE || "LKR",

  // Currency symbol for display
  SYMBOL: process.env.REACT_APP_CURRENCY_SYMBOL || "LKR",

  // Number of decimal places for currency
  DECIMAL_PLACES: parseInt(process.env.REACT_APP_CURRENCY_DECIMALS) || 2,

  // Default display value
  DEFAULT_VALUE: "0.00",

  // Thousand separator
  THOUSAND_SEPARATOR: ",",

  // Decimal separator
  DECIMAL_SEPARATOR: ".",
};

// ============================================
// DATE CONFIGURATION
// ============================================
export const DATE_CONFIG = {
  // Default date format for display
  DISPLAY_FORMAT: process.env.REACT_APP_DATE_FORMAT || "YYYY-MM-DD",

  // API date format
  API_FORMAT: "YYYY-MM-DD",

  // Default empty date value
  DEFAULT_DATE: "0001-01-01",

  // Default empty datetime value
  DEFAULT_DATETIME: "0001-01-01T00:00:00",

  // Locale for date picker
  LOCALE: process.env.REACT_APP_LOCALE || "en",
};

// ============================================
// FILE UPLOAD CONFIGURATION
// ============================================
export const FILE_UPLOAD_CONFIG = {
  // Maximum file size in bytes (default: 5MB)
  MAX_SIZE: parseInt(process.env.REACT_APP_MAX_FILE_SIZE) || 5 * 1024 * 1024,

  // Maximum file size in MB for display
  MAX_SIZE_MB: parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 5,

  // Allowed file types for GRN attachments
  ALLOWED_TYPES: {
    IMAGES: ["image/jpeg", "image/jpg", "image/png", "image/gif"],
    DOCUMENTS: ["application/pdf"],
    ALL: ["image/jpeg", "image/jpg", "image/png", "image/gif", "application/pdf"],
  },

  // File extensions
  ALLOWED_EXTENSIONS: [".jpg", ".jpeg", ".png", ".gif", ".pdf"],
};

// ============================================
// PAGINATION CONFIGURATION
// ============================================
export const PAGINATION_CONFIG = {
  // Default page size
  DEFAULT_PAGE_SIZE: parseInt(process.env.REACT_APP_PAGE_SIZE) || 10,

  // Page size options
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],

  // Maximum items per page
  MAX_PAGE_SIZE: 100,
};

// ============================================
// UI CONFIGURATION
// ============================================
export const UI_CONFIG = {
  // Timeout for navigation (ms)
  NAVIGATION_TIMEOUT: 1000,

  // Default waiting time for operations (ms)
  DEFAULT_WAIT_TIME: 2000,

  // Debounce time for search (ms)
  SEARCH_DEBOUNCE: 500,

  // Auto-save interval (ms)
  AUTO_SAVE_INTERVAL: 30000, // 30 seconds

  // Dropdown max height (px)
  DROPDOWN_MAX_HEIGHT: 150,

  // Icon sizes (px)
  ICON_SIZE: {
    SMALL: 10,
    MEDIUM: 20,
    LARGE: 24,
  },
};

// ============================================
// BUSINESS RULE CONFIGURATION
// ============================================
export const BUSINESS_RULES = {
  // Allow partial shipments by default
  ALLOW_PARTIAL_SHIPMENTS: true,

  // Require batch number for items
  REQUIRE_BATCH_NUMBER: true,

  // Require manufacturing date
  REQUIRE_MFD: false,

  // Require expiry date
  REQUIRE_EXP: false,

  // Auto-calculate taxes
  AUTO_CALCULATE_TAX: true,

  // Allow negative inventory
  ALLOW_NEGATIVE_INVENTORY: false,

  // Minimum approval levels
  MIN_APPROVAL_LEVELS: 1,

  // Maximum approval levels
  MAX_APPROVAL_LEVELS: 5,
};

// ============================================
// API CONFIGURATION
// ============================================
export const API_CONFIG = {
  // Request timeout (ms)
  TIMEOUT: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,

  // Retry attempts
  RETRY_ATTEMPTS: parseInt(process.env.REACT_APP_API_RETRY) || 3,

  // Retry delay (ms)
  RETRY_DELAY: 1000,
};

// ============================================
// NOTIFICATION CONFIGURATION
// ============================================
export const NOTIFICATION_CONFIG = {
  // Auto-hide duration (ms)
  AUTO_HIDE_DURATION: 3000,

  // Position
  POSITION: {
    VERTICAL: "top",
    HORIZONTAL: "right",
  },

  // Default severity levels
  SEVERITY: {
    SUCCESS: "success",
    ERROR: "error",
    WARNING: "warning",
    INFO: "info",
  },
};

// ============================================
// VALIDATION CONFIGURATION
// ============================================
export const VALIDATION_CONFIG = {
  // Maximum decimal places for percentage
  PERCENTAGE_DECIMALS: 2,

  // Maximum decimal places for amount
  AMOUNT_DECIMALS: 2,

  // Maximum decimal places for quantity
  QUANTITY_DECIMALS: 2,

  // Minimum percentage value
  MIN_PERCENTAGE: 0,

  // Maximum percentage value
  MAX_PERCENTAGE: 100,

  // Minimum amount value
  MIN_AMOUNT: 0,

  // Maximum amount value (for validation)
  MAX_AMOUNT: 999999999.99,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get currency formatted string
 * @param {number} amount - Amount to format
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return CURRENCY_CONFIG.DEFAULT_VALUE;
  }

  return `${CURRENCY_CONFIG.SYMBOL} ${parseFloat(amount).toFixed(
    CURRENCY_CONFIG.DECIMAL_PLACES
  )}`;
};

/**
 * Get file size in MB
 * @param {number} bytes - File size in bytes
 * @returns {string} - File size in MB
 */
export const getFileSizeMB = (bytes) => {
  return (bytes / (1024 * 1024)).toFixed(2);
};

/**
 * Check if file type is allowed
 * @param {string} fileType - MIME type of file
 * @returns {boolean} - True if allowed
 */
export const isFileTypeAllowed = (fileType) => {
  return FILE_UPLOAD_CONFIG.ALLOWED_TYPES.ALL.includes(fileType);
};

/**
 * Get environment-specific configuration
 * @returns {string} - Current environment
 */
export const getEnvironment = () => {
  return process.env.REACT_APP_ENV || process.env.NODE_ENV || "development";
};

/**
 * Check if running in development mode
 * @returns {boolean} - True if development
 */
export const isDevelopment = () => {
  return getEnvironment() === "development";
};

/**
 * Check if running in production mode
 * @returns {boolean} - True if production
 */
export const isProduction = () => {
  return getEnvironment() === "production";
};
